// @file For rendering Tldraw canvas in Vue component
import { captureException } from '@@/bits/error_tracker'
import window from '@@/bits/global'
import { tailwindFullConfig } from '@@/bits/tailwind'
import EraserTool from '@@/bits/tldraw/EraserTool'
import InteractTool from '@@/bits/tldraw/InteractTool'
import PadletAttachmentShapeUtil from '@@/bits/tldraw/PadletAttachmentShape'
import PadletHalfEllipseShapeUtil from '@@/bits/tldraw/PadletHalfEllipseShape'
import PadletJamboardDrawGroupShapeUtil from '@@/bits/tldraw/PadletJamboardDrawGroupShape'
import { ShapeIndicator } from '@@/bits/tldraw/ShapeIndicator'
import type { PageMeta } from '@@/bits/whiteboard'
import {
  BackgroundType,
  BACKGROUND_TYPE_TO_URL_MAPPING,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  MAX_FRAME_LIMIT,
} from '@@/bits/whiteboard'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useTldrawStore } from '@@/reacthooks/useTldrawStore'
import { storeToRefs } from 'pinia'
import { useEffect, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import type { Editor, TLRecord } from 'tldraw'
import { Tldraw, track, useEditor, useMenuClipboardEvents } from 'tldraw'
import { onMounted } from 'vue'

const DrawingCanvas = ({
  onMount,
  onError,
  wallId,
  userId,
  canIViewShape,
}: {
  wallId: string
  userId: string
  onMount: (editor: Editor) => void
  onError: (errorMessage?: string) => void
  canIViewShape: (record: TLRecord) => boolean
}): React.JSX.Element => {
  const { store, stopRealtimeUpdates } = useTldrawStore({ wallId, userId, canIViewShape, onError })

  return (
    <Tldraw
      shapeUtils={[PadletAttachmentShapeUtil, PadletHalfEllipseShapeUtil, PadletJamboardDrawGroupShapeUtil]}
      onMount={onMount}
      store={store}
      tools={[EraserTool, InteractTool]}
      components={{
        ShapeIndicator,
        ContextMenu: null,
        // OnTheCanvas ensures the background will scale with the camera zoom, while also not being affected by the tools
        OnTheCanvas: Background,
        ErrorFallback: createCustomErrorFallback({ onError, stopRealtimeUpdates }),
      }}
      assetUrls={{
        fonts: {
          // We use the same font for all font types to reduce loading unnecessary fonts
          // draw: 'https://padlet.net/fonts/monaspace/neon/MonaspaceNeon-SemiBold.woff2',
          // monospace: 'https://padlet.net/fonts/monaspace/neon/MonaspaceNeon-SemiBold.woff2',
          // sansSerif: 'https://padlet.net/fonts/monaspace/neon/MonaspaceNeon-SemiBold.woff2',
          // serif: 'https://padlet.net/fonts/monaspace/neon/MonaspaceNeon-SemiBold.woff2',
          draw: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Medium.woff2',
          monospace: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Medium.woff2',
          sansSerif: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Medium.woff2',
          serif: 'https://padlet.net/fonts/shantell-sans/Shantell_Sans-Normal-Medium.woff2',
        },
      }}
      options={{
        // Tldraw default is 40
        maxPages: MAX_FRAME_LIMIT,
      }}
      hideUi
    >
      <SetupHooks />
    </Tldraw>
  )
}

export const createCustomErrorFallback =
  ({ onError, stopRealtimeUpdates }) =>
  ({ error }) => {
    const errorMessage = error instanceof Error ? error.message : String(error)
    const errorStack = error instanceof Error ? error.stack : null

    useEffect(() => {
      // We want to avoid the case where the data on the client was corrupted but it still pushes an update to the backend
      // and corrupts the data we store in the database
      stopRealtimeUpdates()
      onError()
      captureException(`Sandbox error: ${errorMessage}`, { context: errorStack })
    }, [])

    // We don't want to render the default error boundary because it shows tldraw specific copy
    // and it also has a "reset data" button which could be destructive.
    return <></>
  }

/**
 * OnTheCanvas does not differentiate between pages. By default, it applies for all pages.
 * In order to support different backgrounds for each frame, we store the backgroundType on the page.meta object and listen for changes to it here.
 */
const Background = track((): React.JSX.Element => {
  const editor = useEditor()
  const currentPage = editor.getCurrentPage()
  const currentPageMeta = useMemo((): PageMeta => currentPage.meta, [currentPage])
  const currentBackgroundType = currentPageMeta.backgroundType
  let currentBackgroundImageUrl = ''
  let backgroundStyle = {}
  if (currentBackgroundType === BackgroundType.Custom) {
    currentBackgroundImageUrl =
      currentPageMeta.customBackgroundUrl ?? BACKGROUND_TYPE_TO_URL_MAPPING[BackgroundType.Plain]
    backgroundStyle = {
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
    }
  } else {
    currentBackgroundImageUrl = BACKGROUND_TYPE_TO_URL_MAPPING[currentBackgroundType ?? BackgroundType.Plain]
  }
  const isTransparentBackground = currentBackgroundType === BackgroundType.Frosted

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 0,
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        backgroundImage: `url(${currentBackgroundImageUrl})`,
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        backgroundColor: isTransparentBackground ? 'transparent' : tailwindFullConfig.theme.colors.grey[50],
        backdropFilter: isTransparentBackground ? 'blur(12px)' : '',
        ...backgroundStyle,
      }}
    ></div>
  )
})

/**
 * Most of the hooks that tldraw provides require the editor to be loaded.
 * Since hooks can't be called conditionally, and we use Vue everywhere else,
 * the easiest way to call tldraw hooks is to call them in <Tldraw /> children.
 */
const SetupHooks = (): React.JSX.Element => {
  const menuClipboardEvents = useMenuClipboardEvents()

  useEffect(() => {
    window.tldrawMenuClipboardEvents = menuClipboardEvents
  }, [])

  return <></>
}

export const useTldrawCanvas = ({
  elementId,
  onMount,
  onError,
}: {
  elementId: string
  onMount: (editor: Editor) => void
  onError: () => void
}): void => {
  const surfaceStore = useSurfaceStore()
  const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
  const { wallId, user } = storeToRefs(surfaceStore)

  onMounted(() => {
    if (elementId == null) {
      throw new Error('Element id is required')
    }
    const domNode = document.getElementById(elementId) as HTMLElement
    if (domNode == null) {
      throw new Error(`Element with id ${elementId} not found`)
    }
    const root = createRoot(domNode)
    root.render(
      <DrawingCanvas
        onMount={onMount}
        onError={onError}
        wallId={wallId.value.toString()}
        userId={user.value.id.toString()}
        canIViewShape={whiteboardPermissionsStore.canIViewShape}
      />,
    )
  })
}
